import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Building2, MessageCircleOff, Clock, BadgeCheck, Calendar, Wrench, MapPin, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import OrgTeamPanel from "@/components/OrgTeamPanel";

/** Studio dashboard
 *  - Lists the facilities the org ticked at signup, grouped by category.
 *  - Each facility renders its own Support System card — the domain
 *    tools relevant to that facility (Booking Calendar, LUT Library,
 *    Shot Tracker, etc.). Read-only scaffolding for now; live tools
 *    ship per-facility in later sprints.
 *  - Legacy monolithic keys (`sound` → `mixing_studio`, `post` →
 *    `edit_suite`) are resolved via the alias map returned from
 *    `/api/meta/studio-types`.
 */
function LocationCard({ org }) {
  const lat = org?.geo?.lat;
  const lng = org?.geo?.lng;
  const hasCoords = typeof lat === "number" && typeof lng === "number";
  const listingHref = `/directory/orgs?type=studio_infra&q=${encodeURIComponent(org.brand_name || "")}`;

  return (
    <div className="mt-6 cs-card rounded-2xl p-5" data-testid="studio-location-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
            <MapPin size={11} className="inline mr-1" /> Location · Public Listing
          </div>
          <div className="mt-1 text-sm text-white/80">
            {[org.registered_city, org.registered_state].filter(Boolean).join(", ") || "City not set"}
          </div>
          {org.registered_address && (
            <div className="text-[11px] text-white/55 mt-0.5">{org.registered_address}</div>
          )}
        </div>
        <Link to={listingHref} data-testid="studio-view-public-listing"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-white/15 text-[10px] tracking-[0.22em] uppercase text-white/75 hover:bg-white/[0.05]">
          View Public Listing <ExternalLink size={11} />
        </Link>
      </div>
      {hasCoords ? (
        <div className="mt-4">
          <iframe
            title="Studio location"
            data-testid="studio-map-embed"
            src={`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`}
            className="w-full h-56 rounded-lg border border-white/10"
            loading="lazy"
          />
          <div className="mt-2 flex items-center gap-3 text-[11px] text-white/50">
            <span>Coordinates: {lat.toFixed(4)}, {lng.toFixed(4)}</span>
            {org.google_maps_url && (
              <a href={org.google_maps_url} target="_blank" rel="noreferrer"
                className="text-emerald-300 hover:underline">Open in Google Maps ↗</a>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 text-[11px] text-white/55" data-testid="studio-map-missing">
          No coordinates saved yet. Add your Google Maps share link from
          Settings so crew can navigate to your studio in one tap.
        </div>
      )}
    </div>
  );
}

export default function StudioDashboard() {
  const [org, setOrg] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    (async () => {
      const [{ data: orgResp }, { data: types }] = await Promise.all([
        api.get("/onboard/me/organization"),
        api.get("/meta/studio-types"),
      ]);
      setOrg(orgResp.organization);
      setMeta(types);
    })();
  }, []);

  // Resolve the org's stored facility keys (may include legacy keys)
  // into canonical facility rows, deduped.
  const facilities = useMemo(() => {
    if (!org || !meta?.types) return [];
    const typeByKey = Object.fromEntries((meta.types || []).map((t) => [t.key, t]));
    const aliases = meta.aliases || {};
    const seen = new Set();
    const out = [];
    for (const raw of org.studio_facility_types || []) {
      const canon = typeByKey[raw] ? raw : aliases[raw] || raw;
      if (!typeByKey[canon] || seen.has(canon)) continue;
      seen.add(canon);
      out.push({ ...typeByKey[canon],
                 tools: (meta.support_system || {})[canon] || [] });
    }
    return out;
  }, [org, meta]);

  // Group by category for a scannable dashboard layout.
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const f of facilities) {
      const cat = f.category || "other";
      if (!buckets.has(cat)) {
        buckets.set(cat, { key: cat, label: f.category_label || cat, items: [] });
      }
      buckets.get(cat).items.push(f);
    }
    return Array.from(buckets.values());
  }, [facilities]);

  if (!org) return <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55">Loading dashboard…</div>;
  if (org.type !== "studio") {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="cs-card rounded-2xl p-10 text-center">
          <Building2 size={32} className="mx-auto text-white/40 mb-4" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Studio Dashboard</h1>
          <p className="mt-3 text-sm text-white/65">Your org is registered as &ldquo;{org.type}&rdquo;, not a studio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="studio-dashboard">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Studio</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">{org.brand_name}</h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        <span>{org.legal_name}</span>
        {org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
            <BadgeCheck size={10} className="inline mr-1" /> Verified
          </span>
        )}
      </div>

      {/* Comms trust pillar callout */}
      <div className="mt-6 cs-card rounded-2xl p-4 border border-amber-500/30 flex items-start gap-3" data-testid="comms-rule-banner">
        <MessageCircleOff size={18} className="text-amber-300 shrink-0 mt-0.5" />
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-amber-200 font-semibold">
            Designation-only comms · Active work blocks only
          </div>
          <p className="mt-1 text-[12px] text-white/65 leading-relaxed">
            Production crew can only message your staff by designation (e.g. &ldquo;Sound Engineer · Floor 2&rdquo;),
            never personal contact. Messaging unlocks only while the room is active on the producer&rsquo;s
            project. Outside that window the platform hard-blocks calls and texts.
          </p>
        </div>
      </div>

      {/* Facilities — grouped by category */}
      <div className="mt-8" data-testid="studio-facilities-grid">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 mb-3">
          Facilities ({facilities.length})
        </div>
        {grouped.length === 0 && (
          <div className="cs-card rounded-xl p-6 text-center text-white/55" data-testid="facilities-empty-state">
            No facilities configured yet.
          </div>
        )}
        {grouped.map((g) => (
          <div key={g.key} className="mt-6" data-testid={`facility-group-${g.key}`}>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2">
              {g.label}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {g.items.map((f) => (
                <FacilityCard key={f.key} facility={f} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Team roster (real invite system) */}
      <div className="mt-10">
        <OrgTeamPanel org={org} />
      </div>
    </div>
  );
}

function FacilityCard({ facility }) {
  return (
    <div className="cs-card rounded-xl p-5" data-testid={`facility-card-${facility.key}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-base uppercase tracking-tight">{facility.label}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mt-0.5">
            {facility.tools.length} tools
          </div>
        </div>
        <Calendar size={14} className="text-white/45" />
      </div>
      {facility.blurb && (
        <div className="mt-2 text-[11px] text-white/55 leading-relaxed">{facility.blurb}</div>
      )}
      <div className="mt-4 space-y-1.5"
        data-testid={`facility-support-system-${facility.key}`}>
        {facility.tools.length === 0 && (
          <div className="text-[11px] text-white/40 italic">Support system loading…</div>
        )}
        {facility.tools.map((tool) => (
          <div key={tool.key}
            className="flex items-start gap-2 text-[12px] group cursor-default"
            data-testid={`facility-tool-${facility.key}-${tool.key}`}>
            <Wrench size={10} className="text-white/30 shrink-0 mt-1 group-hover:text-[#F5B82E]/70 transition-colors" />
            <div className="flex-1 min-w-0">
              <div className="text-white/80 font-medium truncate">{tool.label}</div>
              <div className="text-[10px] text-white/45 truncate">{tool.blurb}</div>
            </div>
            <span className="text-[8px] tracking-[0.22em] uppercase text-white/30">
              <Clock size={8} className="inline mr-0.5" /> Soon
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
